import { useMemo } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  MultiSelect,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowRight,
  IconCircleCheck,
  IconCircleHalf2,
  IconPlus,
  IconX,
} from '@tabler/icons-react';
import { useFormatGroups, useTaxonomy } from '@/api/queries';
import type { ObjectiveStrength, TaxonomyTerm, TermRole, Vocabulary } from '@/api/types';
import { VOCAB_LABEL } from '@/lib/vocab';
import { Section } from './DetailsTab';
import { useProgrammeFormContext } from './formContext';
import type { FormTerm } from './model';

function useTermMap(vocab: Vocabulary) {
  const q = useTaxonomy(vocab);
  const map = useMemo(() => new Map((q.data ?? []).map((t) => [t.id, t])), [q.data]);
  return { terms: q.data ?? [], map, isPending: q.isPending };
}

/** Read/write the form's terms for one vocabulary. */
function useVocabTerms(vocab: Vocabulary) {
  const form = useProgrammeFormContext();
  const all = form.getValues().terms;
  const chosen = all.filter((t) => t.vocabulary === vocab);
  const set = (next: FormTerm[]) => {
    form.setFieldValue('terms', [
      ...form.getValues().terms.filter((t) => t.vocabulary !== vocab),
      ...next,
    ]);
  };
  return { chosen, set };
}

export function ClassificationTab() {
  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        Classification drives recommendations and their explanations. Interests are what a child
        likes; parent objectives are what a grown-up hopes they get from watching. Required to
        approve: one or more primary interests, one or more subjects, at least one format and one
        energy level.
      </Text>
      <Section
        title="What it’s about"
        description="Interests are child-facing (“Animals”); subjects are the catalogue topics they map to (“Biology”)."
      >
        <RoleTermList
          vocab="interest"
          help="Primary = what the programme is mainly about. Secondary = also features."
        />
        <SubjectsPicker />
      </Section>
      <Section title="How it feels">
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <FormatsPicker />
          <SingleTerm
            vocab="energy"
            required
            description="Used by calmer-viewing and movement objectives."
          />
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <MultiTerm vocab="style" />
          <MultiTerm vocab="educational_value" />
          <MultiTerm vocab="genre" />
        </SimpleGrid>
      </Section>
      <ObjectivesSection />
      <RelationshipPanel />
    </Stack>
  );
}

function RoleTermList({ vocab, help }: { vocab: Vocabulary; help: string }) {
  const { terms, map } = useTermMap(vocab);
  const { chosen, set } = useVocabTerms(vocab);
  const available = terms
    .filter((t) => !chosen.some((c) => c.term_id === t.id))
    .map((t) => ({ value: t.id, label: t.label }));
  const label = VOCAB_LABEL[vocab];

  return (
    <Stack gap={6}>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      <Text size="xs" c="dimmed">
        {help}
      </Text>
      {chosen.length > 0 && (
        <Stack gap={4} role="list" aria-label={`Chosen ${label.toLowerCase()}`}>
          {chosen.map((c) => {
            const name = map.get(c.term_id)?.label ?? 'Unknown term';
            return (
              <Group key={c.term_id} gap="xs" role="listitem" wrap="nowrap">
                <Text size="sm" w={160} truncate>
                  {name}
                </Text>
                <SegmentedControl
                  size="xs"
                  aria-label={`Role of ${name}`}
                  value={c.role}
                  onChange={(v) =>
                    set(
                      chosen.map((x) =>
                        x.term_id === c.term_id ? { ...x, role: v as TermRole } : x,
                      ),
                    )
                  }
                  data={[
                    { value: 'primary', label: 'Primary' },
                    { value: 'secondary', label: 'Secondary' },
                  ]}
                />
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Remove ${name}`}
                  onClick={() => set(chosen.filter((x) => x.term_id !== c.term_id))}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Group>
            );
          })}
        </Stack>
      )}
      <Select
        aria-label={`Add ${label.toLowerCase()}`}
        placeholder={`Add ${label.toLowerCase().replace(/s$/, '')}…`}
        searchable
        data={available}
        value={null}
        onChange={(id) =>
          id &&
          set([
            ...chosen,
            {
              term_id: id,
              vocabulary: vocab,
              role: chosen.some((x) => x.role === 'primary') ? 'secondary' : 'primary',
            },
          ])
        }
        maw={320}
      />
    </Stack>
  );
}

function SubjectsPicker() {
  const { terms: interests } = useTermMap('interest');
  const { map: subjectMap } = useTermMap('subject');
  const { chosen: chosenInterests } = useVocabTerms('interest');
  const { chosen, set } = useVocabTerms('subject');
  const suggested = useMemo(() => {
    const ids = new Set<string>();
    for (const ci of chosenInterests) {
      for (const s of interests.find((i) => i.id === ci.term_id)?.subject_ids ?? []) ids.add(s);
    }
    return [...ids].filter((id) => !chosen.some((c) => c.term_id === id) && subjectMap.has(id));
  }, [chosenInterests, interests, chosen, subjectMap]);

  return (
    <Stack gap={6}>
      <RoleTermList
        vocab="subject"
        help="The first primary subject is used for subject questions and the related rail."
      />
      {suggested.length > 0 && (
        <Group gap={6}>
          <Text size="xs" c="dimmed">
            Suggested from the interests:
          </Text>
          {suggested.map((id) => (
            <Button
              key={id}
              size="compact-xs"
              variant="light"
              color="leaf"
              leftSection={<IconPlus size={12} />}
              onClick={() =>
                set([
                  ...chosen,
                  {
                    term_id: id,
                    vocabulary: 'subject',
                    role: chosen.length ? 'secondary' : 'primary',
                  },
                ])
              }
            >
              {subjectMap.get(id)?.label}
            </Button>
          ))}
        </Group>
      )}
    </Stack>
  );
}

function SingleTerm({
  vocab,
  required,
  description,
}: {
  vocab: Vocabulary;
  required?: boolean;
  description?: string;
}) {
  const { terms } = useTermMap(vocab);
  const { chosen, set } = useVocabTerms(vocab);
  return (
    <Select
      label={VOCAB_LABEL[vocab]}
      description={description}
      required={required}
      clearable
      searchable
      data={terms.map((t) => ({ value: t.id, label: t.label }))}
      value={chosen[0]?.term_id ?? null}
      onChange={(id) => set(id ? [{ term_id: id, vocabulary: vocab, role: 'primary' }] : [])}
      error={chosen.length > 1 ? 'Choose exactly one.' : undefined}
    />
  );
}

/** Formats, grouped as children see them: typically one on-screen format plus a programme type. */
function FormatsPicker() {
  const { terms } = useTermMap('format');
  const groups = useFormatGroups();
  const { chosen, set } = useVocabTerms('format');
  const option = (t: TaxonomyTerm) => ({ value: t.id, label: t.label });
  const data = [
    ...(groups.data ?? []).map((g) => ({
      group: g.label,
      items: terms.filter((t) => t.format_group === g.slug).map(option),
    })),
    {
      group: 'No group',
      items: terms
        .filter((t) => !t.format_group || !groups.data?.some((g) => g.slug === t.format_group))
        .map(option),
    },
  ].filter((g) => g.items.length > 0);
  return (
    <MultiSelect
      label={VOCAB_LABEL.format}
      description="One from each group that fits, e.g. Animation and Songs."
      required
      searchable
      clearable
      data={data}
      value={chosen.map((c) => c.term_id)}
      onChange={(ids) =>
        set(
          ids.map(
            (id) =>
              chosen.find((c) => c.term_id === id) ?? {
                term_id: id,
                vocabulary: 'format',
                role: 'primary',
              },
          ),
        )
      }
    />
  );
}

function MultiTerm({ vocab }: { vocab: Vocabulary }) {
  const { terms } = useTermMap(vocab);
  const { chosen, set } = useVocabTerms(vocab);
  return (
    <MultiSelect
      label={VOCAB_LABEL[vocab]}
      description="Recommended"
      searchable
      clearable
      data={terms.map((t) => ({ value: t.id, label: t.label }))}
      value={chosen.map((c) => c.term_id)}
      onChange={(ids) =>
        set(
          ids.map(
            (id) =>
              chosen.find((c) => c.term_id === id) ?? {
                term_id: id,
                vocabulary: vocab,
                role: 'primary',
              },
          ),
        )
      }
    />
  );
}

function ObjectivesSection() {
  const form = useProgrammeFormContext();
  const { terms, map } = useTermMap('parent_objective');
  const objectives = form.getValues().objectives;
  const available = terms
    .filter((t) => !objectives.some((o) => o.objective_term_id === t.id))
    .map((t) => ({ value: t.id, label: t.parent_label ?? t.label }));

  return (
    <Section
      title="Parent objectives"
      description="What a grown-up could hope their child gets from this. Strong support puts it in the “interest × objective” row; some support counts towards objective picks only."
    >
      {objectives.length === 0 && (
        <Text size="sm" c="dimmed">
          No objectives yet. Recommended, so this programme can appear for grown-ups’ goals.
        </Text>
      )}
      <Stack gap="xs" role="list" aria-label="Supported objectives">
        {objectives.map((o, i) => {
          const name =
            map.get(o.objective_term_id)?.parent_label ??
            map.get(o.objective_term_id)?.label ??
            'Unknown objective';
          return (
            <Paper key={o.objective_term_id} withBorder p="xs" radius="sm" role="listitem">
              <Group gap="sm" wrap="wrap" align="flex-end">
                <Text size="sm" fw={500} w={150}>
                  {name}
                </Text>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed" id={`strength-${o.objective_term_id}`}>
                    Strength
                  </Text>
                  <SegmentedControl
                    size="xs"
                    aria-labelledby={`strength-${o.objective_term_id}`}
                    data={[
                      { value: 'strong', label: 'Strong' },
                      { value: 'some', label: 'Some' },
                    ]}
                    {...form.getInputProps(`objectives.${i}.strength`)}
                  />
                </Stack>
                <TextInput
                  size="xs"
                  label="Note (optional)"
                  placeholder="Why it supports this"
                  style={{ flex: 1, minWidth: 180 }}
                  {...form.getInputProps(`objectives.${i}.note`)}
                />
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Remove ${name}`}
                  onClick={() => form.removeListItem('objectives', i)}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Group>
            </Paper>
          );
        })}
      </Stack>
      <Select
        aria-label="Add parent objective"
        placeholder="Add objective…"
        searchable
        data={available}
        value={null}
        onChange={(id) =>
          id &&
          form.insertListItem('objectives', {
            objective_term_id: id,
            strength: 'strong' as ObjectiveStrength,
            note: '',
          })
        }
        maw={320}
      />
    </Section>
  );
}

/**
 * Brief §4.8: "Identify suitable relationships between child interests and parent objectives".
 * Shows, for each interest × objective pair on this programme, how the homepage will use it.
 */
function RelationshipPanel() {
  const form = useProgrammeFormContext();
  const { map: interestMap } = useTermMap('interest');
  const { map: objMap } = useTermMap('parent_objective');
  const values = form.getValues();
  const interests = values.terms.filter((t) => t.vocabulary === 'interest');
  const objectives = values.objectives;

  return (
    <Section
      title="Interest × objective"
      description="How this programme bridges what a child likes and what their grown-up wants. Children who like one of these interests, whose grown-up chose a strongly supported objective, may see it in the “interest × objective” row."
    >
      {interests.length === 0 || objectives.length === 0 ? (
        <Text size="sm" c="dimmed">
          Add at least one interest and one parent objective to see the matches. Example: an
          animated animal programme (interest Animals, subject Biology) that strongly supports
          Science.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={420}>
          <Table
            withTableBorder
            withColumnBorders
            fz="sm"
            aria-label="Interest and objective matches"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Child likes…</Table.Th>
                {objectives.map((o) => (
                  <Table.Th key={o.objective_term_id}>
                    Grown-up wants {label(objMap.get(o.objective_term_id))}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {interests.map((it) => (
                <Table.Tr key={it.term_id}>
                  <Table.Td>
                    <Group gap={6}>
                      <Text size="sm" fw={500}>
                        {label(interestMap.get(it.term_id))}
                      </Text>
                      <Badge size="xs" variant="outline" color="gray">
                        {it.role}
                      </Badge>
                    </Group>
                  </Table.Td>
                  {objectives.map((o) => (
                    <Table.Td key={o.objective_term_id}>
                      <MatchCell strength={o.strength} interestRole={it.role} />
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Group gap="md">
        <Legend
          icon={<IconCircleCheck size={12} />}
          color="leaf"
          text="Overlap row: strong objective"
        />
        <Legend
          icon={<IconCircleHalf2 size={12} />}
          color="sky"
          text="Objective picks only: some support"
        />
      </Group>
    </Section>
  );
}

function label(t: TaxonomyTerm | undefined) {
  return t?.parent_label ?? t?.label ?? '…';
}

function MatchCell({
  strength,
  interestRole,
}: {
  strength: ObjectiveStrength;
  interestRole: TermRole;
}) {
  if (strength === 'strong')
    return (
      <Tooltip
        label={
          interestRole === 'primary'
            ? 'Strong match: eligible for the interest × objective row.'
            : 'Eligible for the overlap row; a secondary interest ranks lower.'
        }
      >
        <Group gap={4} wrap="nowrap">
          <ThemeIcon size={18} radius="xl" color="leaf" variant="light" aria-hidden>
            <IconCircleCheck size={12} />
          </ThemeIcon>
          <Text size="xs">Overlap row</Text>
          <IconArrowRight size={12} aria-hidden />
        </Group>
      </Tooltip>
    );
  return (
    <Group gap={4} wrap="nowrap">
      <ThemeIcon size={18} radius="xl" color="sky" variant="light" aria-hidden>
        <IconCircleHalf2 size={12} />
      </ThemeIcon>
      <Text size="xs">Objective picks</Text>
    </Group>
  );
}

function Legend({ icon, color, text }: { icon: React.ReactNode; color: string; text: string }) {
  return (
    <Group gap={4}>
      <ThemeIcon size={16} radius="xl" color={color} variant="light" aria-hidden>
        {icon}
      </ThemeIcon>
      <Text size="xs" c="dimmed">
        {text}
      </Text>
    </Group>
  );
}
